/**
 * Validates the `plusTracker` object stored on an application user-state item (TEC-744, spec
 * 6.12). Used when reading user state (client and server) and before writing, so a malformed or
 * oversized value from another client can never break My Jobs. Unknown keys are dropped.
 */
import {
  PLUS_TRACKER_LIMITS as L,
  type MailInterviewDate,
  type PlusOfferDetails,
  type PlusTrackerContact,
  type PlusTrackerData,
  type PlusTrackerDebrief,
  type PlusTrackerInterview,
  type PlusTrackerNote,
} from '../../types/plus-myjobs';

function str(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : undefined;
}

function isoDateTime(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  return Number.isFinite(Date.parse(value)) ? value : undefined;
}

function num(value: unknown, min: number, max: number): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  if (value < min || value > max) return undefined;
  return value;
}

function id(value: unknown): string | undefined {
  return typeof value === 'string' && /^[\w.:@+-]{1,80}$/.test(value) ? value : undefined;
}

function sanitizeInterview(raw: unknown): PlusTrackerInterview | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const at = isoDateTime(r.at);
  const iid = id(r.id);
  if (!at || !iid) return null;
  const out: PlusTrackerInterview = { id: iid, at };
  const place = str(r.place, L.placeChars);
  if (place) out.place = place;
  const reminder = num(r.reminderMinutes, 0, 60 * 24 * 14);
  if (reminder !== undefined) out.reminderMinutes = Math.round(reminder);
  const note = str(r.note, L.textChars);
  if (note) out.note = note;
  Object.assign(out, distanceFields(r));
  return out;
}

/** The server-written travel distance of an interview place (INT-7), kept through client rewrites. */
function distanceFields(r: Record<string, unknown>): Pick<PlusTrackerInterview, 'distanceKm' | 'distanceSource' | 'distancePlace'> {
  const km = num(r.distanceKm, 0, 5000);
  const place = str(r.distancePlace, L.placeChars);
  if (km === undefined || !place) return {};
  return { distanceKm: Math.round(km * 10) / 10, distanceSource: r.distanceSource === 'route' ? 'route' : 'estimate', distancePlace: place };
}

function sanitizeContact(raw: unknown): PlusTrackerContact | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const name = str(r.name, 120);
  if (!name) return undefined;
  const out: PlusTrackerContact = { name };
  const role = str(r.role, 120);
  if (role) out.role = role;
  const email = str(r.email, 200);
  if (email && /^[^\s@]+@[^\s@]+$/.test(email)) out.email = email;
  const phone = str(r.phone, 40);
  if (phone) out.phone = phone;
  return out;
}

function sanitizeNote(raw: unknown): PlusTrackerNote | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const nid = id(r.id);
  const at = isoDateTime(r.at);
  const text = str(r.text, L.noteChars);
  return nid && at && text ? { id: nid, at, text } : null;
}

function sanitizeDebrief(raw: unknown): PlusTrackerDebrief | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const interviewId = id(r.interviewId);
  const at = isoDateTime(r.at);
  if (!interviewId || !at) return null;
  const out: PlusTrackerDebrief = { interviewId, at, notes: str(r.notes, L.noteChars) ?? '' };
  if (r.feeling === 'good' || r.feeling === 'mixed' || r.feeling === 'bad') out.feeling = r.feeling;
  const sent = isoDateTime(r.thankYouSentAt);
  if (sent) out.thankYouSentAt = sent;
  return out;
}

export function sanitizeOffer(raw: unknown): PlusOfferDetails | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const enteredAt = isoDateTime(r.enteredAt) ?? new Date().toISOString();
  const out: PlusOfferDetails = { enteredAt };
  const gross = num(r.monthlyGross, 1, 100000);
  if (gross !== undefined) out.monthlyGross = Math.round(gross);
  const holidays = num(r.holidayDays, 0, 100);
  if (holidays !== undefined) out.holidayDays = Math.round(holidays * 2) / 2;
  const benefits = str(r.benefits, L.benefitsChars);
  if (benefits) out.benefits = benefits;
  const office = num(r.officeDaysPerWeek, 0, 7);
  if (office !== undefined) out.officeDaysPerWeek = Math.round(office * 2) / 2;
  const trial = num(r.trialMonths, 0, 12);
  if (trial !== undefined) out.trialMonths = Math.round(trial * 2) / 2;
  const municipality = str(r.municipality, 60);
  if (municipality) out.municipality = municipality;
  if (typeof r.replyDeadline === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(r.replyDeadline) && Number.isFinite(Date.parse(r.replyDeadline))) {
    out.replyDeadline = r.replyDeadline;
  }
  return out;
}

/** Returns a clean tracker object, or undefined when nothing valid is left. */
export function sanitizePlusTracker(raw: unknown): PlusTrackerData | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const out: PlusTrackerData = {};

  if (typeof r.followUpAt === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(r.followUpAt) && Number.isFinite(Date.parse(r.followUpAt))) {
    out.followUpAt = r.followUpAt;
  }
  if (Array.isArray(r.interviews)) {
    const list = r.interviews.map(sanitizeInterview).filter((x): x is PlusTrackerInterview => x !== null).slice(0, L.interviews);
    if (list.length) out.interviews = list;
  }
  const contact = sanitizeContact(r.contact);
  if (contact) out.contact = contact;
  if (Array.isArray(r.notes)) {
    const list = r.notes.map(sanitizeNote).filter((x): x is PlusTrackerNote => x !== null).slice(-L.notes);
    if (list.length) out.notes = list;
  }
  if (Array.isArray(r.debriefs)) {
    const list = r.debriefs.map(sanitizeDebrief).filter((x): x is PlusTrackerDebrief => x !== null).slice(-L.debriefs);
    if (list.length) out.debriefs = list;
  }
  const offer = sanitizeOffer(r.offer);
  if (offer) out.offer = offer;
  if (Array.isArray(r.dismissed)) {
    const list = r.dismissed.filter((x): x is string => typeof x === 'string' && x.length <= 80).slice(-L.dismissed);
    if (list.length) out.dismissed = list;
  }
  return Object.keys(out).length ? out : undefined;
}

/** Short random id for interviews and notes (client side; not security relevant). */
export function newTrackerId(prefix = 'x'): string {
  return `${prefix}${Math.random().toString(36).slice(2, 10)}`;
}

/** Validates the `interviewDates` array the mail ingest writes; keeps at most 20, drops anything malformed. */
export function sanitizeMailInterviewDates(raw: unknown): MailInterviewDate[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: MailInterviewDate[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const iid = id(r.id);
    const at = typeof r.at === 'string' && Number.isFinite(Date.parse(r.at)) ? r.at : undefined;
    if (!iid || !at) continue;
    const entry: MailInterviewDate = { id: iid, at };
    const place = str(r.place, L.placeChars);
    if (place) entry.place = place;
    const link = str(r.link, 300);
    if (link && /^https?:\/\//i.test(link)) entry.link = link;
    Object.assign(entry, distanceFields(r));
    out.push(entry);
  }
  return out.length ? out.slice(-20) : undefined;
}
